import { useEffect, useRef } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Calculator, MessageCircle, Share2 } from 'lucide-react'
import { z } from 'zod'
import { DEFAULT_REPAYMENT_METHOD, MAX_TERM_PERIODS, PAYMENT_FREQUENCIES, REPAYMENT_METHODS, SimulateLoanSchema, nominalDueDate, type IsoDate } from '@tumin/domain'
import { EmptyState } from '@/components/empty-state'
import { BrandMark } from '@/components/layout/brand-mark'
import { WovenBand } from '@/components/layout/woven-band'
import { SectionCard } from '@/components/section-card'
import { Button } from '@/components/ui/button'
import { SimulatorForm } from '@/features/simulator/simulator-form'
import { SimulationResultView, type SimulationResult } from '@/features/simulator/simulation-result'
import { api } from '@/lib/api'
import { useAuthStore } from '@/lib/auth'
import { useBranding } from '@/lib/branding'
import { orgToday } from '@/lib/dates'
import { applyApiErrors, useZodForm } from '@/lib/form'
import { notifySuccess } from '@/lib/notify'
import { simulateWithFallback } from '@/lib/offline/local-simulation'
import { ROUTES } from '@/lib/routes'

/** Valores iniciales razonables para un microcrédito rural (Q 10,000 a 12 cuotas mensuales al 24 %). */
const DEFAULTS = {
  principal: 1_000_000,
  annualInterestRate: 2_400,
  termPeriods: 12,
  frequency: 'monthly',
} as const

/**
 * Parámetros compartibles en la URL (en unidades del API: centavos y puntos
 * básicos). Valores inválidos se descartan en lugar de romper la página.
 */
const simulatorSearchSchema = z.object({
  monto: z.coerce.number().int().positive().optional().catch(undefined),
  tasa: z.coerce.number().int().min(0).optional().catch(undefined),
  cuotas: z.coerce.number().int().min(1).optional().catch(undefined),
  frecuencia: z.enum(PAYMENT_FREQUENCIES).optional().catch(undefined),
  fecha: z.string().optional().catch(undefined),
  gracia: z.coerce.number().int().min(0).optional().catch(undefined),
  comision: z.coerce.number().int().min(0).optional().catch(undefined),
  cargos: z.coerce.number().int().min(0).optional().catch(undefined),
  forma: z.enum(REPAYMENT_METHODS).optional().catch(undefined),
  /** Plan estacional como lista separada por comas en puntos básicos: "0,0,5000,5000". */
  plan: z.string().optional().catch(undefined),
})
type SimulatorSearch = z.infer<typeof simulatorSearchSchema>

/** Separador del plan estacional en la URL compartible. */
const PLAN_SEPARATOR = ','

/** Lee el plan estacional de la URL; ignora valores que no sean enteros. */
function planFromSearch(plan: string | undefined): number[] | null {
  if (!plan) return null
  const shares = plan.split(PLAN_SEPARATOR).map(Number)
  return shares.length <= MAX_TERM_PERIODS && shares.every(Number.isInteger) ? shares : null
}

export const Route = createFileRoute('/simulador')({
  validateSearch: simulatorSearchSchema,
  component: SimulatorPage,
})

function SimulatorPage() {
  const branding = useBranding()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const signedIn = useAuthStore((s) => s.employee !== null)
  const today = orgToday()
  const frequency = search.frecuencia ?? DEFAULTS.frequency

  const form = useZodForm(SimulateLoanSchema, {
    defaultValues: {
      principal: search.monto ?? DEFAULTS.principal,
      annualInterestRate: search.tasa ?? DEFAULTS.annualInterestRate,
      termPeriods: search.cuotas ?? DEFAULTS.termPeriods,
      frequency,
      firstPaymentDate: search.fecha ?? nominalDueDate(today, frequency, 1),
      principalGracePeriods: search.gracia ?? 0,
      originationFeeRate: search.comision ?? 0,
      fixedCharges: search.cargos ?? 0,
      repaymentMethod: search.forma ?? DEFAULT_REPAYMENT_METHOD,
      principalPlan: planFromSearch(search.plan),
    },
  })

  const queryClient = useQueryClient()
  const simulate = useMutation({
    // Sin señal (p. ej. en una feria rural) el plan se calcula en el teléfono con la misma regla.
    mutationFn: (input: z.output<typeof SimulateLoanSchema>) =>
      simulateWithFallback(() => api.public.post<SimulationResult>('/public/simulate', input), input, undefined, queryClient),
    meta: { silentError: true },
    onError: (error) => applyApiErrors(form, error),
  })

  const submit = (values: z.output<typeof SimulateLoanSchema>) => {
    const next: SimulatorSearch = {
      monto: values.principal,
      tasa: values.annualInterestRate,
      cuotas: values.termPeriods,
      frecuencia: values.frequency,
      fecha: values.firstPaymentDate,
      ...(values.principalGracePeriods ? { gracia: values.principalGracePeriods } : {}),
      ...(values.originationFeeRate ? { comision: values.originationFeeRate } : {}),
      ...(values.fixedCharges ? { cargos: values.fixedCharges } : {}),
      ...(values.repaymentMethod !== DEFAULT_REPAYMENT_METHOD ? { forma: values.repaymentMethod } : {}),
      ...(values.principalPlan ? { plan: values.principalPlan.join(PLAN_SEPARATOR) } : {}),
    }
    void navigate({ search: next, replace: true })
    simulate.mutate(values)
  }

  // Un enlace compartido con monto ya trae el resultado calculado.
  const autoRan = useRef(false)
  useEffect(() => {
    if (autoRan.current || search.monto === undefined) return
    autoRan.current = true
    void form.handleSubmit(submit)()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, [])

  const share = async () => {
    const url = window.location.href
    const title = `Simulación de crédito — ${branding.tradeName}`
    if (typeof navigator.share === 'function') {
      await navigator.share({ title, url }).catch(() => undefined)
      return
    }
    await navigator.clipboard.writeText(url)
    notifySuccess('Enlace copiado', 'Péguelo en WhatsApp o en un correo.')
  }
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`Simulación de crédito — ${branding.tradeName}: ${typeof window === 'undefined' ? '' : window.location.href}`)}`

  return (
    <div className="min-h-dvh bg-background">
      <header className="bg-sidebar text-sidebar-foreground">
        <WovenBand size="sm" />
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-8">
          <BrandMark size="sm" showName tone="inverse" />
          <Button asChild variant="outline" className="min-h-11 border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground">
            <Link to={signedIn ? ROUTES.dashboard : ROUTES.login}>{signedIn ? 'Ir al panel' : 'Iniciar sesión'}</Link>
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-8 sm:py-10">
        <div className="mb-6">
          <p className="text-sm text-muted-foreground">Sin compromiso · tarda un minuto</p>
          <h1 className="mt-1 font-heading text-[1.75rem] font-bold leading-tight tracking-[-0.03em] sm:text-[2.375rem]">Simulador de crédito</h1>
          <p className="mt-1 text-[15px] text-muted-foreground">Calcule su cuota y el costo total antes de solicitar.</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,25rem)_minmax(0,1fr)]">
          <SectionCard title="Su crédito" className="h-fit">
            <SimulatorForm form={form} onSubmit={submit} isPending={simulate.isPending} minDate={today as IsoDate} />
          </SectionCard>
          <div className="min-w-0 space-y-4">
            {simulate.data ? (
              <>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" className="min-h-11" onClick={() => void share()}>
                    <Share2 /> Compartir
                  </Button>
                  <Button asChild variant="outline" className="min-h-11">
                    <a href={whatsappUrl} target="_blank" rel="noreferrer">
                      <MessageCircle /> WhatsApp
                    </a>
                  </Button>
                </div>
                <SimulationResultView result={simulate.data} frequency={simulate.variables?.frequency} />
              </>
            ) : (
              <div className="rounded-[18px] border border-dashed border-border bg-card">
                <EmptyState icon={Calculator} title="Su plan de pagos aparecerá aquí" description="Ingrese el monto, la tasa y el plazo, y presione Calcular." />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
